import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, Plus, Search, Filter, X, Zap } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClubsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [amountFilter, setAmountFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contribution_amount: '',
    contribution_frequency: 'weekly',
    currency: 'USD'
  });

  useEffect(() => {
    loadClubs();
    if (searchParams.get('action') === 'create') {
      setShowCreateDialog(true);
    }
    
    const amountParam = searchParams.get('amount');
    if (amountParam) {
      setAmountFilter(amountParam);
      toast.info(`Showing clubs with $${amountParam} contribution`);
    }
  }, [searchParams]);

  const loadClubs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clubs`, { withCredentials: true });
      setClubs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Load clubs error:', error);
      setLoading(false);
    }
  };

  const handleCreateClub = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/clubs`,
        formData,
        { withCredentials: true }
      );

      toast.success('Club created successfully!');
      setShowCreateDialog(false);
      setFormData({
        name: '',
        description: '',
        contribution_amount: '',
        contribution_frequency: 'weekly',
        currency: 'USD'
      });
      loadClubs();
      navigate(`/clubs/${response.data.club_id}`);
    } catch (error) {
      console.error('Create club error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create club');
    } finally {
      setCreating(false);
    }
  };

  const filteredClubs = clubs.filter(club => {
    const matchesSearch = club.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      club.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesAmount = !amountFilter || 
      club.contribution_amount === parseFloat(amountFilter);
    
    return matchesSearch && matchesAmount;
  });

  const clearAmountFilter = () => {
    setAmountFilter('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('amount');
    setSearchParams(newParams);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-12 h-12 text-lime-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans selection:bg-lime-500/30 overflow-x-hidden relative">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px', pointerEvents: 'none' }}></div>
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
          <div>
            <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Susu Clubs
            </h1>
            <p className="text-neutral-400 font-medium">Join a rotation or start your own circle.</p>
          </div>
          <Button
            data-testid="create-club-header-button"
            onClick={() => setShowCreateDialog(true)}
            className="rounded-full bg-lime-500 hover:bg-lime-400 text-black font-black px-8 py-6 h-auto"
          >
            <Plus className="mr-2 w-5 h-5 stroke-[3]" />
            Create Club
          </Button>
        </div>

        <div className="mb-8">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500 group-focus-within:text-lime-400 transition-colors" />
            <Input
              data-testid="search-clubs-input"
              type="text"
              placeholder="Search clubs by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 rounded-2xl bg-[#111111] border-white/10 text-white placeholder:text-neutral-600 focus-visible:ring-lime-500 focus-visible:border-lime-500 h-14 text-base"
            />
          </div>
          
          {amountFilter && (
            <div className="mt-4 flex items-center gap-2">
              <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 px-4 py-2 rounded-full font-bold">
                <Filter className="w-3 h-3 mr-2" />
                Showing ${amountFilter} clubs
              </Badge>
              <Button
                data-testid="clear-amount-filter-button"
                variant="ghost"
                size="sm"
                onClick={clearAmountFilter}
                className="rounded-full hover:bg-white/10 hover:text-white text-neutral-400 w-8 h-8 p-0 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {filteredClubs.length === 0 ? (
          <Card className="border-white/10 bg-[#111111] rounded-3xl">
            <CardContent className="py-20 text-center flex flex-col items-center">
              <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-10 h-10 text-neutral-600" />
              </div>
              <h3 className="text-2xl font-black text-white mb-2 tracking-tight uppercase" style={{ fontFamily: 'Unbounded, sans-serif' }}>No clubs found</h3>
              <p className="text-neutral-400 mb-8 font-medium">
                {searchQuery ? 'Try a different search term or clear filters.' : 'Be the first to create a rotation circle!'}
              </p>
              {!searchQuery && (
                <Button
                  data-testid="create-first-club-button"
                  onClick={() => setShowCreateDialog(true)}
                  className="rounded-full bg-lime-500 hover:bg-lime-400 text-black font-black px-8 py-6 h-auto"
                >
                  <Plus className="mr-2 w-5 h-5 stroke-[3]" />
                  Create Your First Club
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClubs.map((club) => (
              <Card
                key={club.club_id}
                data-testid={`club-list-item-${club.club_id}`}
                className="border-white/10 bg-[#111111] rounded-3xl hover:border-lime-500/50 hover:shadow-[0_0_30px_rgba(132,204,22,0.15)] transition-all cursor-pointer group flex flex-col h-full overflow-hidden relative"
                onClick={() => navigate(`/clubs/${club.club_id}`)}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-lime-500/5 blur-[50px] group-hover:bg-lime-500/10 transition-colors pointer-events-none"></div>
                <CardHeader className="border-b border-white/5 pb-4 relative z-10">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-xl font-black text-white group-hover:text-lime-400 transition-colors">
                      {club.name}
                    </CardTitle>
                    <span className={`shrink-0 inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                      club.status === 'active' ? 'bg-lime-500/10 text-lime-400 border border-lime-500/20' : 'bg-white/5 text-neutral-400 border border-white/10'
                    }`}>
                      {club.status}
                    </span>
                  </div>
                  <CardDescription className="line-clamp-2 text-neutral-400 mt-2 font-medium">{club.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-6 flex-grow flex flex-col justify-end relative z-10">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between bg-black/40 rounded-2xl p-4 border border-white/5">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Contribution</span>
                        <span className="font-black text-white text-lg tracking-tight" style={{ fontFamily: 'Unbounded, monospace' }}>
                          ${club.contribution_amount} <span className="text-sm text-neutral-400 font-medium tracking-normal">/ {club.contribution_frequency}</span>
                        </span>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-lime-500/10 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-lime-400" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between px-2">
                      <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Total Pool</span>
                      <span className="font-black text-amber-400 text-lg tracking-tight" style={{ fontFamily: 'Unbounded, monospace' }}>
                        ${club.total_pool.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between px-2">
                      <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Members</span>
                      <span className="font-bold text-white flex items-center">
                        <Users className="w-4 h-4 mr-2 text-cyan-400" />
                        {club.members.length}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px] bg-[#111111] border-white/10 text-white p-0 overflow-hidden rounded-3xl">
          <div className="p-6 border-b border-white/5 bg-[#1A1A1A]">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black uppercase tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>Create New Club</DialogTitle>
            </DialogHeader>
          </div>
          <form onSubmit={handleCreateClub} className="p-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-bold uppercase tracking-widest text-neutral-400">Club Name</Label>
              <Input
                data-testid="create-club-name-input"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Weekend Warriors"
                required
                className="rounded-xl bg-[#0A0A0A] border-white/10 focus-visible:ring-lime-500 h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-xs font-bold uppercase tracking-widest text-neutral-400">Description</Label>
              <Textarea
                data-testid="create-club-description-input"
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Tell members what this club is about..."
                required
                className="rounded-xl bg-[#0A0A0A] border-white/10 focus-visible:ring-lime-500 min-h-[100px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-xs font-bold uppercase tracking-widest text-neutral-400">Amount ($)</Label>
                <Input
                  data-testid="create-club-amount-input"
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.10"
                  max="1000"
                  value={formData.contribution_amount}
                  onChange={(e) => setFormData({ ...formData, contribution_amount: e.target.value })}
                  placeholder="0.00"
                  required
                  className="rounded-xl bg-[#0A0A0A] border-white/10 focus-visible:ring-lime-500 h-12 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="frequency" className="text-xs font-bold uppercase tracking-widest text-neutral-400">Frequency</Label>
                <Select
                  value={formData.contribution_frequency}
                  onValueChange={(value) => setFormData({ ...formData, contribution_frequency: value })}
                >
                  <SelectTrigger data-testid="create-club-frequency-select" className="rounded-xl bg-[#0A0A0A] border-white/10 h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-white/10 text-white rounded-xl">
                    <SelectItem value="daily" className="focus:bg-white/10 focus:text-white">Daily</SelectItem>
                    <SelectItem value="weekly" className="focus:bg-white/10 focus:text-white">Weekly</SelectItem>
                    <SelectItem value="biweekly" className="focus:bg-white/10 focus:text-white">Bi-weekly</SelectItem>
                    <SelectItem value="monthly" className="focus:bg-white/10 focus:text-white">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 hidden">
              <Label htmlFor="currency">Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger data-testid="create-club-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="NGN">NGN (₦)</SelectItem>
                  <SelectItem value="GHS">GHS (₵)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4 pt-4 border-t border-white/5 mt-6">
              <Button
                data-testid="cancel-create-club-button"
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1 rounded-full h-12 border-white/10 hover:bg-white/5 hover:text-white font-bold"
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-create-club-button"
                type="submit"
                className="flex-1 rounded-full h-12 bg-lime-500 hover:bg-lime-400 text-black font-black"
                disabled={creating}
              >
                {creating ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Club'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
